package com.bulut.mecitogullari.ui.screens.craftsmandetail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.parseDecimalAmount
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.Craftsman
import com.bulut.mecitogullari.data.model.CraftsmanBalance
import com.bulut.mecitogullari.data.model.CraftsmanEntry
import com.bulut.mecitogullari.data.model.CraftsmanEntryType
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.data.model.craftsmanBalance
import com.bulut.mecitogullari.data.repository.BarterRepository
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

data class CraftsmanDetailUiState(
    val craftsman: Craftsman? = null,
    val balance: CraftsmanBalance? = null,
    val entries: List<CraftsmanEntry> = emptyList(),
    val unitsGiven: List<ApartmentUnit> = emptyList(),
    val projects: List<Project> = emptyList(),
    /** Units that can still be given (not sold, not bartered). */
    val availableUnits: List<ApartmentUnit> = emptyList(),
    val projectNames: Map<String, String> = emptyMap(),
)

class CraftsmanDetailViewModel(
    private val craftsmanId: String,
    private val barterRepository: BarterRepository,
    private val unitRepository: UnitRepository,
    projectRepository: ProjectRepository,
) : ViewModel() {

    private val _craftsmanDeleted = MutableStateFlow(false)
    val craftsmanDeleted: StateFlow<Boolean> = _craftsmanDeleted.asStateFlow()

    val uiState: StateFlow<CraftsmanDetailUiState> = combine(
        barterRepository.craftsmen,
        barterRepository.entriesFor(craftsmanId),
        unitRepository.units,
        projectRepository.projects,
    ) { craftsmen, entries, units, projects ->
        val craftsman = craftsmen.firstOrNull { it.id == craftsmanId }
        val given = units.filter { it.barterCraftsmanId == craftsmanId }
        CraftsmanDetailUiState(
            craftsman = craftsman,
            balance = craftsman?.let { craftsmanBalance(it, entries, given) },
            entries = entries.sortedByDescending { it.dateEpochMillis },
            unitsGiven = given.sortedBy { it.number },
            projects = projects,
            availableUnits = units.filter { it.status != UnitStatus.SOLD && it.barterCraftsmanId == null },
            projectNames = projects.associate { it.id to it.name },
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = CraftsmanDetailUiState(),
    )

    /** Per-type completed counts (yanlışsa geri çekilebilir). */
    fun updateCompletedWork(completedTexts: Map<String, String>) {
        val craftsman = uiState.value.craftsman ?: return
        val updatedTypes = craftsman.effectiveWorkTypes.map { workType ->
            val parsed = parseDecimalAmount(completedTexts[workType.type] ?: "")
            workType.copy(completedCount = (parsed ?: workType.completedCount).coerceAtLeast(0.0))
        }
        viewModelScope.launch {
            barterRepository.upsertCraftsman(
                // Legacy single-type fields are migrated into workTypes here.
                craftsman.copy(
                    workTypes = updatedTypes,
                    agreedWorkCount = 0.0,
                    workUnitPrice = 0.0,
                    completedWorkCount = 0.0,
                )
            )
        }
    }

    fun addEntry(type: CraftsmanEntryType, amount: Double, dateEpochMillis: Long, description: String) {
        if (amount <= 0) return
        viewModelScope.launch {
            barterRepository.upsertEntry(
                CraftsmanEntry(
                    id = newId(),
                    craftsmanId = craftsmanId,
                    type = type,
                    dateEpochMillis = dateEpochMillis,
                    description = description.trim().ifBlank {
                        when (type) {
                            CraftsmanEntryType.EXTRA_WORK -> Localization.strings.extraWorkLine
                            CraftsmanEntryType.CASH_PAYMENT -> Localization.strings.pdfCashPayment
                            CraftsmanEntryType.INVOICE -> Localization.strings.entryInvoiceTitle
                        }
                    },
                    amount = amount,
                )
            )
        }
    }

    /** Müteahhidin bu ustadan istediği toplam fatura tutarı (0 = takibi kapat). */
    fun updateRequiredInvoice(amount: Double) {
        val craftsman = uiState.value.craftsman ?: return
        viewModelScope.launch {
            barterRepository.upsertCraftsman(
                craftsman.copy(requiredInvoiceAmount = amount.coerceAtLeast(0.0))
            )
        }
    }

    fun deleteEntry(entryId: String) {
        viewModelScope.launch {
            barterRepository.deleteEntry(entryId)
        }
    }

    /**
     * Gives an apartment as barter: marked SOLD but flagged so it never
     * produces cash income (payments stay untouched/empty).
     */
    fun giveUnit(unit: ApartmentUnit, valueTl: Double) {
        val craftsman = uiState.value.craftsman ?: return
        viewModelScope.launch {
            unitRepository.updateUnit(
                unit.copy(
                    status = UnitStatus.SOLD,
                    salePrice = valueTl,
                    saleDateEpochMillis = com.bulut.mecitogullari.core.util.todayUtcMidnightMillis(),
                    buyerName = craftsman.name,
                    barterCraftsmanId = craftsmanId,
                    payments = emptyList(),
                )
            )
        }
    }

    /** Reverts a bartered apartment back to the market. */
    fun takeBackUnit(unit: ApartmentUnit) {
        viewModelScope.launch {
            unitRepository.updateUnit(
                unit.copy(
                    status = UnitStatus.AVAILABLE,
                    saleDateEpochMillis = null,
                    buyerName = "",
                    barterCraftsmanId = null,
                    payments = emptyList(),
                )
            )
        }
    }

    fun deleteCraftsman() {
        viewModelScope.launch {
            // Unlink bartered units but keep them sold to the person.
            uiState.value.unitsGiven.forEach { unit ->
                unitRepository.updateUnit(unit.copy(barterCraftsmanId = null))
            }
            barterRepository.deleteCraftsman(craftsmanId)
            _craftsmanDeleted.value = true
        }
    }
}
